import axios from 'axios';

// IMPORTANT: Ensure API_BASE_URL is correct for your setup.
const API_BASE_URL = 'https://roofenet.vsb.cz/annotation_platform_backend/api'; // No trailing slash

// Create an Axios instance with default settings
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send cookies with requests (needed for session auth)
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// --- Auth ---
export const login = (username, password) => {
  return api.post('/login.php', { username, password });
};

export const logout = () => {
  return api.post('/logout.php');
};

export const checkAuthStatus = () => {
  return api.get('/status.php');
};

// --- Images ---
/** Fetches the list of available images (metadata) */
export const getImages = () => {
  // This endpoint returns { image_id, original_filename, width, height }
  return api.get('/images.php');
};

/** Fetches metadata details for a single image */
export const getImageDetails = (imageId) => {
  // Request JSON metadata using the metadata=true parameter
  return api.get(`/images.php?id=${imageId}&metadata=true`).then(response => {
    // response.data should contain { image_id, original_filename, width, height }
    if (response.data && response.data.width && response.data.height) {
      // The 'fullUrl' should point to the endpoint that serves the raw image data
      // which is the SAME endpoint but WITHOUT '&metadata=true'
      response.data.fullUrl = `${API_BASE_URL}/images.php?id=${imageId}`; // URL to fetch the blob

      response.data.size = [response.data.width, response.data.height];
    } else {
      console.warn("Image details response missing required data (width/height):", response.data);
      // Set defaults or handle error state in component
      response.data.fullUrl = null;
      response.data.size = null;
    }
    return response; // Return the augmented response object
  }).catch(error => {
    console.error(`Error fetching image details for ID ${imageId}:`, error);
    // Rethrow the error so the calling component can handle it (e.g., show error message)
    throw error;
  });
};


/** Fetches the next image for the current user to annotate */
export const getNextImageForAnnotation = () => {
  // Returns image details directly { image_id, original_filename, width, height }
  return api.get('/getNextImage.php').then(response => {
    // Construct fullUrl here pointing to the image serving endpoint
    if (response.data && response.data.image_id && response.data.width && response.data.height) {
      response.data.fullUrl = `${API_BASE_URL}/images.php?id=${response.data.image_id}`; // URL to fetch the blob
      response.data.size = [response.data.width, response.data.height];
    } else if (response.data && response.status !== 404) { // Only warn if not the "no images" message
      console.warn("Get next image response missing required data:", response.data);
      response.data.fullUrl = null;
      response.data.size = null;
    }
    // Make sure to return response even on failure/missing data so component can react
    return response;
  });
};

/**Fetches a validated image for manual purposes */
export const getValidatedImageForManual = () => {
  //Assumes there is a new endpoint, this needs to be implemented in backend

  return api.get('/getValidatedImageForManual.php').then(response => {
    // Construct fullUrl here pointing to the image serving endpoint
    if (response.data && response.data.image_id && response.data.width && response.data.height) {
      response.data.fullUrl = `${API_BASE_URL}/images.php?id=${response.data.image_id}`; // URL to fetch the blob
      response.data.size = [response.data.width, response.data.height];
    } else {
      console.warn("Get validated image response missing required data:", response.data);
      response.data.fullUrl = null;
      response.data.size = null;
    }
    // Make sure to return response even on failure/missing data so component can react
    return response;
  });
};


// --- Annotations ---
/** Fetches annotations based on imageId, setId, or setType */
export const getAnnotations = (params = {}) => {
  const backendParams = {};
  if (params.imageId !== undefined) backendParams.image_id = params.imageId;
  if (params.setId !== undefined) backendParams.set_id = params.setId;
  if (params.setType !== undefined) backendParams.set_type = params.setType;
  return api.get('/annotations.php', { params: backendParams }); // Pass mapped params object
};

/** Saves a new set of annotations for an image */
export const saveAnnotations = (imageId, annotations) => {
  // Prepare data matching backend expectations (POST /annotations.php)
  const payload = {
    image_id: parseInt(imageId, 10), // Ensure image_id is integer
    annotations: annotations.map(ann => ({
      // Properties must match EXACTLY what backend's $_POST processing expects
      annotationType: ann.annotationType, // 'roof' or 'obstruction'
      label: ann.label || '',             // string label, ensure not null/undefined
      tilt_angle: ann.annotationType === 'roof' && ann.tilt_angle !== null ? parseFloat(ann.tilt_angle) : null,
      box_2d: ann.box_2d                   // [ymin, xmin, ymax, xmax] integer array
    }))
  };
  // Add validation here before sending?
  const hasInvalidBox = payload.annotations.some(ann =>
    !ann.box_2d || ann.box_2d.length !== 4 || ann.box_2d.some(coord => typeof coord !== 'number' || !Number.isInteger(coord))
  );
  if (hasInvalidBox) {
    console.error("Attempting to save annotations with invalid box_2d format:", payload.annotations);
    // Prevent API call by returning a rejected Promise or throwing an error
    return Promise.reject(new Error("Invalid bounding box data detected. Cannot save."));
  }

  return api.post('/annotations.php', payload); // POST sends JSON body
};


// --- Admin ---

/** Uploads image and metadata files (Requires FormData) */
export const uploadBulkData = (formData) => {
  return api.post('/admin/upload.php', formData, {
    headers: {
      // Let browser set Content-Type for FormData
      'Content-Type': undefined,
    },
  });
};

/** Fetches metadata for all annotation sets available for a specific image */
export const getAllSetsForImage = (imageId) => {
  // Ensure imageId is passed correctly
  return api.get(`/admin/imageSets.php?image_id=${imageId}`);
};


/** Marks an annotation set as validated */
export const validateAnnotationSet = (setId) => {
  return api.post('/admin/validate.php', { set_id: parseInt(setId, 10) });
};

/** Fetches validated data for export */
export const exportValidatedData = (format = 'json_per_image') => {
  return api.get(`/admin/export.php?format=${format}`, {
    responseType: 'blob', // Expect blob data for file download
  }).then(response => {
    // Attempt to parse JSON only if the format is JSON and blob type indicates JSON
    if (format === 'json_per_image' && response.data && response.data.type === 'application/json') {
      // Return a promise that resolves with the response after parsing
      return response.data.text().then(text => {
        try {
          response.jsonData = JSON.parse(text); // Store parsed JSON
        } catch (e) {
          console.error("Failed to parse JSON blob:", e);
          response.jsonData = null; // Indicate parsing failure
        }
        return response; // Return the original response object augmented with jsonData
      });
    }
    // For CSV or non-JSON blobs, return the response directly
    return response;
  });
};

/** Fetches user rankings */
export const getRankings = () => {
  return api.get('/admin/rankings.php');
};


export default api;